import { useCallback, useSyncExternalStore } from 'react'
import moment from 'jalali-moment'

/**
 * «امتیاز از چه تاریخی» — the manager's filter on the score (pwa#131).
 *
 * A filter, not a setting: picking a day re-ranks on the spot and nothing is
 * saved on the server. The first version was a per-fund setting behind a
 * «ذخیره» button; the manager who asked for it wanted to look at a window,
 * not to change the fund's rule, and every other manager of the fund would
 * have seen the ranking move under them.
 *
 * One value per fund, shared by every screen that ranks its members — the
 * grant wizard, فهرست اعضا, کارنامهٔ عضو, the requested-loans tab — so the
 * rank a manager reads on a member's page is the rank they tapped in the
 * list. Kept in sessionStorage: it survives going into a member and back,
 * and a window chosen last week does not silently reorder today's list.
 *
 * `null` is the whole history. Otherwise a Gregorian `YYYY-MM-DD`, which is
 * what the backend's `?score_from=` takes (`TScoreFrom`, in the fund types).
 */

const KEY = (fundId: string) => `hamyan:score-from:${fundId}`
const GREGORIAN = /^\d{4}-\d{2}-\d{2}$/

/**
 * The earliest day the backend takes (`_SCORE_START_MIN`): anything before
 * it is a Jalali date sent as Gregorian — «1404-07-01» parses fine as AD.
 */
export const SCORE_FROM_MIN = '2000-01-01'

// the source of truth; sessionStorage only seeds it, so a blocked or full
// store still gives a working filter for as long as the page is open
const memory = new Map<string, TScoreFrom>()
const listeners = new Set<() => void>()

const valid = (value: string | null): TScoreFrom =>
    value &&
    GREGORIAN.test(value) &&
    moment(value, 'YYYY-MM-DD', true).isValid() &&
    value >= SCORE_FROM_MIN &&
    // a day that has since become «the future» cannot happen, but a hand
    // edit can, and the backend 400s it
    !moment(value, 'YYYY-MM-DD').isAfter(moment(), 'day')
        ? value
        : null

export const readScoreFrom = (fundId?: string | number | null): TScoreFrom => {
    if (fundId === undefined || fundId === null || fundId === '') return null
    const id = String(fundId)
    if (!memory.has(id)) {
        let stored: string | null = null
        try {
            stored = sessionStorage.getItem(KEY(id))
        } catch {
            stored = null
        }
        memory.set(id, valid(stored))
    }
    return memory.get(id) ?? null
}

export const writeScoreFrom = (
    fundId: string | number | null | undefined,
    value: TScoreFrom
) => {
    if (fundId === undefined || fundId === null || fundId === '') return
    const id = String(fundId)
    const next = valid(value)
    memory.set(id, next)
    try {
        if (next) sessionStorage.setItem(KEY(id), next)
        else sessionStorage.removeItem(KEY(id))
    } catch {
        // the in-memory value above still applies for this page's life
    }
    listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

/** `[window, setWindow]` for one fund's score. */
export default function useScoreFrom(fundId?: string | number | null) {
    const value = useSyncExternalStore(subscribe, () => readScoreFrom(fundId))
    const set = useCallback(
        (next: TScoreFrom) => writeScoreFrom(fundId, next),
        [fundId]
    )
    return [value, set] as const
}

/** Test-only: forget every fund's window. */
export const resetScoreFromForTests = () => {
    memory.clear()
    try {
        Object.keys(sessionStorage)
            .filter((key) => key.startsWith('hamyan:score-from:'))
            .forEach((key) => sessionStorage.removeItem(key))
    } catch {
        // nothing to clear
    }
}
